<template>
  <ul v-if="loading" class="gs-results-list gs-skeleton" role="status" aria-live="polite">
    <li v-for="i in 3" :key="`skeleton-${i}`">
      <div class="gs-result-item gs-user-section">
        <zm-skeleton-item variant="circle" style="width: 32px; height: 32px" />
        <p class="gs-user-name">
          <zm-skeleton-item variant="text" :percent="50" />
        </p>
        <p class="gs-user-email">
          <zm-skeleton-item variant="text" :percent="40" />
        </p>
      </div>
    </li>
  </ul>
  <ul
    v-else-if="usersList.length > 0"
    class="gs-results-list"
    :aria-label="t('aria.usersResults', [usersList.length])">
    <li v-for="user in usersList" :key="user.user_id">
      <a
        class="gs-result-item gs-user-section"
        href="javascript: void(0)"
        @click="onNavigation(user)">
        <zm-avatar
          aria-hidden="true"
          size="md"
          class="gs-result-avatar"
          :src="user.picUrl"
          :color="getColorWithJID(getShortName(user.name))"
          :full-name="user.name"
          :short-name="getShortName(user.name)" />
        <p class="gs-user-name">
          <zm-highlight-text
            :key="`${user.name}::${activeQuery}`"
            :content="user.name || ''"
            :highlight="highlightKeywords" />
        </p>
        <p class="gs-user-email">
          <zm-highlight-text
            :key="`${user.email}::${activeQuery}`"
            :content="user.email || ''"
            :highlight="highlightKeywords" />
        </p>
      </a>
    </li>
  </ul>
  <div v-else class="gs-empty" role="status">
    <img src="/assets/search-empty.png" width="200" height="200" alt="" />
    <p class="empty-main">{{ $t('searchEmpty') }}</p>
    <p class="empty-sub" v-html="$t('noUsers')"></p>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { ZmAvatar, ZmHighlightText, ZmSkeletonItem } from '@zoom/zoom-ui-vue3'
import { axiosInstance } from '../../../shared/api-helper'
import { toHighlightKeywords } from './highlight.js'
import { trackEvent } from '@/shared/event-tracker'

const { t } = useI18n()

const usersList = ref([])
const loading = ref(false)
const activeQuery = ref('')
const highlightKeywords = computed(() => toHighlightKeywords(activeQuery.value))
let abortController = null

const isAborted = (error, signal) =>
  Boolean(signal?.aborted || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError')

const cancel = () => {
  abortController?.abort()
  abortController = null
  loading.value = false
}

const getColorWithJID = (JID) => {
  let idx = 0
  for (let i = 0; i < JID.length; i++) {
    idx += JID.charCodeAt(i)
  }
  idx %= 8

  const colors = ['green', 'teal', 'steel', 'purple', 'gray', 'yellow', 'orange', 'red']

  return colors[idx]
}

const getShortName = (name) => {
  if (!name) return ''

  return name
    .split(/[\s.]+/)
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('')
}

const search = async (keyword) => {
  abortController?.abort()
  abortController = null

  const nextQuery = keyword?.trim() ?? ''
  if (!nextQuery) {
    activeQuery.value = ''
    usersList.value = []
    loading.value = false
    return 0
  }

  const controller = new AbortController()
  abortController = controller
  const { signal } = controller
  activeQuery.value = nextQuery

  loading.value = true
  try {
    const result = await axiosInstance.get('/account/user/search', {
      params: {
        search_key: nextQuery
      },
      signal
    })

    usersList.value = result.data?.result || []
    return usersList.value.length
  } catch (error) {
    if (isAborted(error, signal)) return 0
    console.error('Search users failed:', error)
    usersList.value = []
    return 0
  } finally {
    if (abortController === controller) loading.value = false
  }
}

const onNavigation = (user) => {
  trackEvent({
    linkText: 'user search',
    linkUrl: 'undefined',
    uiLocation: 'web portal global search - user'
  })
  const url = `/user/${user.user_id}/profile`
  setTimeout(() => {
    window.location.href = url
  }, 100)
}

defineExpose({
  search,
  cancel
})
</script>

<style lang="scss">
.gs-user-section {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto auto;
  column-gap: 12px;
  row-gap: 4px;
  align-items: center;
  text-align: left;

  > :first-child {
    grid-row: 1 / 3;
  }

  .gs-user-name {
    margin: 0;
    line-height: 18px;
    font-weight: 600;
    color: var(--zoom-color-text-stronger-neutral, #323539);
    margin-bottom: 2px;
  }
  .gs-user-email {
    margin: 0;
    line-height: 16px;
    font-size: 12px;
    color: var(--zoom-color-text-strong-neutral, #555b62);
  }
}
.gs-results-list > li + li {
  margin-top: 8px;
}
.gs-skeleton {
  pointer-events: none;

  .gs-user-section {
    cursor: default;
  }
}
.gs-result-avatar {
  border-radius: 10px;
}
</style>
